import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaFileExport, FaFilter } from 'react-icons/fa';
import { MultiSelect } from '../../../../common/multiSelect/MultiSelect';
import { categoryCrud } from '../../../../../api/category';
import { companyCrud } from '../../../../../api/company';
import { yearCrud } from '../../../../../api/yearApi';
import { unitCrud } from '../../../../../api/UnitsApi';
import ReactNotifications, { showApiErrors } from '../../../../common/reactNotifications/ReactNotifications';
import * as utilConstants from "../../../../../utils/constants/utilsConstants";
import { customDownloadToCsv } from '../../../../../api/financeApi';

export const CustomExportModal = ({ isOpen, onClose, sheetId }) => {
    const [exportConfig, setExportConfig] = useState({
        rowStart: 1,
        rowEnd: 1,
        selectedCategories: [],
        selectedUnits: [],
        selectedYears: [],
        selectedCompanies: []
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [years, setYears] = useState([]);
    const [companies, setCompanies] = useState([]);
    const maxRows = 1000;

    useEffect(() => {
        if (isOpen) {
            fetchDropdownData();
        }
    }, [isOpen]);

    const fetchDropdownData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchAllCategories(),
                fetchAllUnits(),
                fetchAllYears(),
                fetchAllCompanies()
            ]);
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCategories = () => {
        return new Promise((resolve) => {
            categoryCrud({ page: false }, 'get', (response) => {
                if (response?.status === true) {
                    const categories = response?.data?.map(item => ({
                        id: item.id,
                        name: item.category_name,
                        code: item.category_code,
                    }));
                    setCategories(categories);
                }
                resolve();
            });
        });
    };

    const fetchAllCompanies = () => {
        return new Promise((resolve) => {
            companyCrud({ page: false }, 'get', (response) => {
                if (response?.status === true) {
                    const companies = response?.data?.map(item => ({
                        id: item.id,
                        name: item.identifier_code,
                    }));
                    setCompanies(companies);
                }
                resolve();
            });
        });
    };

    const fetchAllYears = () => {
        return new Promise((resolve) => {
            yearCrud({ page: false }, 'get', (response) => {
                if (response?.status === true) {
                    const allYears = response?.data?.map(item => ({
                        id: item.id,
                        year: item.year,
                    }));
                    setYears(allYears);
                }
                resolve();
            });
        });
    };

    const fetchAllUnits = () => {
        return new Promise((resolve) => {
            unitCrud({ page: false }, 'get', (response) => {
                if (response?.status === true) {
                    const allUnits = response?.data?.map(item => ({
                        id: item.id,
                        name: item.name,
                    }));
                    setUnits(allUnits);
                }
                resolve();
            });
        });
    };

    const categoryOptions = categories?.map(cat => ({
        value: cat.id,
        label: cat.name
    }));

    const unitOptions = units?.map(unit => ({
        value: unit.id,
        label: unit.name
    }));

    const yearOptions = years?.map(year => ({
        value: year.id,
        label: year.year.toString()
    }));

    const companyOptions = companies?.map(company => ({
        value: company.id,
        label: company.name
    }));

    const validateForm = () => {
        const newErrors = {};
        const start = parseInt(exportConfig.rowStart, 10);
        const end = parseInt(exportConfig.rowEnd, 10);

        if (isNaN(start) || start < 1) {
            newErrors.rowStart = 'Start row must be at least 1';
        }

        if (isNaN(end) || end < start) {
            newErrors.rowEnd = 'End row must be greater than or equal to start row';
        }

        if (end > maxRows) {
            newErrors.rowEnd = `End row cannot exceed ${maxRows}`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleExport = async () => {
        if (!validateForm()) return;

        const payload = {
            row_ids: {
                start: parseInt(exportConfig.rowStart, 10),
                end: parseInt(exportConfig.rowEnd, 10)
            },
            category_ids: exportConfig.selectedCategories.map(c => c.value),
            unit_ids: exportConfig.selectedUnits.map(u => u.value),
            years: exportConfig.selectedYears.map(y => {
                const yearObj = years.find(yr => yr.id === y.value);
                return yearObj ? yearObj.year : y.value;
            }).join(','),
            sheet_company_ids: exportConfig.selectedCompanies.map(c => c.value)
        };

        try {
            setLoading(true);
            customDownloadToCsv(sheetId, payload, (response) => {
                if (response?.status >= 200 && response?.status < 300) {
                    ReactNotifications("Exported Successfully", utilConstants.sucessState200);
                    handleClose();
                } else if (response?.data?.message) {
                    ReactNotifications(response.data.message, utilConstants.getErrorStatus);
                } else if (response?.status) {
                    showApiErrors({
                        message: response.statusText || `Error: ${response.status}`
                    });
                } else {
                    showApiErrors({ message: "No response received" });
                }
            });
        } catch (error) {
            showApiErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setExportConfig({
            rowStart: 1,
            rowEnd: 100,
            selectedCategories: [],
            selectedUnits: [],
            selectedYears: [],
            selectedCompanies: []
        });
        setErrors({});
        onClose();
    };

    return (
        <>
            <Modal show={isOpen} onHide={handleClose} size="lg" centered className="custom-export-modal">
                <Modal.Header closeButton className="border-b-2 border-gray-200">
                    <Modal.Title className="d-flex align-items-center gap-2">
                        <FaFilter style={{ color: '#000' }} />
                        Custom Export
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {loading && (
                        <div className="d-flex align-items-center justify-content-center py-4">
                            <div className="spinner-border" role="status" style={{ color: '#AEF468' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="ms-3">Loading options...</span>
                        </div>
                    )}

                    {!loading && (
                        <div className="space-y-4">
                            <div className="bg-light p-3 rounded border">
                                <h6 className="fw-bold mb-3">Row Range</h6>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label text-sm fw-medium">Start Row</label>
                                        <input
                                            type="number"
                                            value={exportConfig.rowStart}
                                            onChange={(e) => setExportConfig({
                                                ...exportConfig,
                                                rowStart: e.target.value
                                            })}
                                            className={`form-control ${errors.rowStart ? 'is-invalid' : ''}`}
                                        />
                                        {errors.rowStart && (
                                            <div className="invalid-feedback">{errors.rowStart}</div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label text-sm fw-medium">End Row</label>
                                        <input
                                            type="number"
                                            value={exportConfig.rowEnd}
                                            onChange={(e) => setExportConfig({
                                                ...exportConfig,
                                                rowEnd: e.target.value
                                            })}
                                            className={`form-control ${errors.rowEnd ? 'is-invalid' : ''}`}
                                        />
                                        {errors.rowEnd && (
                                            <div className="invalid-feedback">{errors.rowEnd}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3">
                                <h6 className="fw-bold mb-3">Filters (Optional)</h6>

                                <div className="mb-3">
                                    <label className="form-label text-sm fw-medium">Categories</label>
                                    <MultiSelect
                                        options={categoryOptions}
                                        selected={exportConfig.selectedCategories}
                                        onChange={(selected) => setExportConfig({
                                            ...exportConfig,
                                            selectedCategories: selected
                                        })}
                                        placeholder="Select categories..."
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label text-sm fw-medium">Units</label>
                                    <MultiSelect
                                        options={unitOptions}
                                        selected={exportConfig.selectedUnits}
                                        onChange={(selected) => setExportConfig({
                                            ...exportConfig,
                                            selectedUnits: selected
                                        })}
                                        placeholder="Select units..."
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label text-sm fw-medium">Years</label>
                                    <MultiSelect
                                        options={yearOptions}
                                        selected={exportConfig.selectedYears}
                                        onChange={(selected) => setExportConfig({
                                            ...exportConfig,
                                            selectedYears: selected
                                        })}
                                        placeholder="Select years..."
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label text-sm fw-medium">Companies</label>
                                    <MultiSelect
                                        options={companyOptions}
                                        selected={exportConfig.selectedCompanies}
                                        onChange={(selected) => setExportConfig({
                                            ...exportConfig,
                                            selectedCompanies: selected
                                        })}
                                        placeholder="Select companies..."
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-top-2">
                    <Button
                        onClick={handleExport}
                        disabled={loading}
                        className="btn-export d-flex align-items-center !bg-[#AEF468] !border-none !text-black gap-2"
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Exporting...
                            </>
                        ) : (
                            <>
                                Export
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};